import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import styled from 'styled-components';
import client from '../shopify';


const PageContainer = styled.div`
  background-color: #fff;
  padding-bottom: 40px;
`;

const BreadcrumbNav = styled.div`
  display: flex;
  padding: 10px 40px;
  font-size: 0.8em;
  color: #666;
  
  a {
    color: #666;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  span {
    margin: 0 5px;
  }
`;

const Container = styled.div`
  display: flex;
  padding: 0 40px 40px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const ThumbnailsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 80px;
  margin-right: 20px;
  
  @media (max-width: 900px) {
    flex-direction: row;
    width: 100%;
    margin-right: 0;
    margin-bottom: 20px;
    overflow-x: auto;
  }
`;

const ThumbnailImage = styled.div`
  width: 60px;
  height: 60px;
  border: 1px solid ${props => props.active ? '#000' : '#ddd'};
  cursor: pointer;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  
  &:hover {
    border-color: #999;
  }
`;

const ImageSection = styled.div`
  flex: 1.2;
  display: flex;
  justify-content: center;
  align-items: flex-start; /* Align to top instead of center */
  background-color: #fff; /* Fixed typo in color */
  padding: 0;
  margin-right: 40px;
  margin-top: -30px; /* Move image higher on the page */
  
  @media (max-width: 900px) {
    margin-right: 0;
    margin-bottom: 30px;
    margin-top: -15px; /* Less adjustment on mobile */
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
  display: block;
`;

const DetailsSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
  min-width: 0; /* allow shrinking so image column keeps its share */
`;

/* Commented out to fix ESLint warning
const CollectionName = styled.div`
  font-size: 0.9em;
  color: #666;
  margin-bottom: -15px;
`;
*/

const Title = styled.h1`
  font-size: 2em;
  font-weight: 400;
  color: #000;
  margin: 0 0 10px 0;
  line-height: 1.3;
`;

const Price = styled.p`
  font-size: 1.2em;
  font-weight: 400;
  color: #000;
  margin: 0 0 30px 0;
`;

/* Commented out to fix ESLint warning
const ColorSection = styled.div`
  margin-top: 10px;
`;
*/

/* Commented out to fix ESLint warning
const ColorLabel = styled.div`
  font-size: 0.9em;
  text-transform: uppercase;
  margin-bottom: 10px;
  color: #666;
`;
*/

/* Commented out to fix ESLint warning
const ColorOptions = styled.div`
  display: flex;
  gap: 10px;
`;
*/

/* Commented out to fix ESLint warning
const ColorCircle = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: ${props => props.color};
  border: 1px solid #ddd;
  cursor: pointer;
  box-shadow: ${props => props.selected ? '0 0 0 2px #000' : 'none'};
  
  &:hover {
    box-shadow: 0 0 0 1px #999;
  }
`;
*/

/* Commented out to fix ESLint warning
const SizeSection = styled.div`
  margin-top: 30px;
  margin-bottom: 20px;
`;
*/

/* Commented out to fix ESLint warning
const SizeHeader = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 15px;
`;
*/

const SizeLabel = styled.div`
  font-size: 1em;
  text-transform: uppercase;
  color: #000;
  margin-bottom: 10px;
`;

/* Commented out to fix ESLint warning
const SizeGuideLink = styled.a`
  font-size: 0.8em;
  color: #666;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;
*/

const SizeOptions = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;


const SizeButton = styled.button`
  width: 80px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.selected ? '#fff' : '#000'};
  color: ${props => props.selected ? '#000' : '#fff'};
  border: 1px solid #000;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 1em;
  text-align: center;
  opacity: ${props => props.disabled ? 0.5 : 1};
  
  &:hover {
    border-color: ${props => props.disabled ? '#000' : '#666'};
  }
`;


const ErrorMessage = styled.div`
  color: #e53935;
  margin: 10px 0;
  font-size: 0.9em;
`;

const SuccessMessage = styled.div`
  color: #43a047;
  margin: 10px 0;
  font-size: 0.9em;
  font-weight: 500;
`;

const AddToCartButton = styled.button`
  width: 100%;
  padding: 18px;
  background-color: #000;
  color: #fff;
  border: none;
  font-size: 1em;
  cursor: pointer;
  margin-top: 10px;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    background-color: #666;
    cursor: not-allowed;
  }
`;

/* Commented out to fix ESLint warning
const ProductNote = styled.div`
  font-size: 0.8em;
  color: #666;
  margin-top: 15px;
  line-height: 1.5;
`;
*/

const TabsContainer = styled.div`
  margin-top: 40px;
  border-bottom: 1px solid #fff; /* match page background to hide line */
  position: relative; /* Create positioning context for tab content */
  height: 500px; /* Fixed height for the entire tabs section */
  display: flex;
  flex-direction: column;
`;

const TabsHeader = styled.div`
  display: block; /* allow inner to control its own overflow */
`;

const TabsHeaderInner = styled.div`
  display: flex;
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
  border-bottom: 2px solid #000; /* align underline to content width */
  justify-content: flex-start;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  flex-wrap: nowrap; /* do not wrap tabs */
  min-width: 0; /* allow scrolling within flex contexts */
  white-space: nowrap; /* keep all tabs on one line */
  
  /* hide scrollbar where possible */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
  &::-webkit-scrollbar { display: none; } /* WebKit */
`;

const Tab = styled.div`
  padding: 12px 25px;
  font-size: 1em;
  cursor: pointer;
  border-bottom: 2px solid ${props => props.active ? '#000' : 'transparent'};
  text-transform: uppercase;
  font-weight: ${props => props.active ? 'bold' : 'normal'};
  margin-bottom: -2px;
  flex: 0 0 auto; /* prevent shrinking */
  white-space: nowrap; /* keep label on one line */
  
  &:hover {
    border-bottom-color: ${props => props.active ? '#000' : '#999'};
  }

  @media (max-width: 768px) {
    padding: 10px 16px; /* match content rhythm */
    font-size: 0.95em;
    border-bottom-color: transparent; /* hide active underline on mobile; use header underline only */
  }
`;

const TabContent = styled.div`
  padding: 24px 0;
  font-size: 1em;
  line-height: 1.6;
  display: block;
  flex: 1;
  overflow-y: auto;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  text-align: left;
  
  &.full-width {
    display: block;
    width: 100%;
  }
  
  h3 {
    margin-top: 0;
    font-size: 1.1em;
    font-weight: 500;
    margin-bottom: 16px;
  }
  
  ul {
    padding-left: 0;
    margin: 0;
    list-style: none;
  }
  
  li {
    margin-bottom: 8px;
    position: relative;
    padding-left: 16px;
  }
  
  li:before {
    content: '\u2014';
    position: absolute;
    left: 0;
    color: #666;
  }
  
  /* Paragraph body copy (used in Shipping & Returns) */
  p {
    margin: 0 0 16px; /* 2 x 8px rhythm for paragraphs */
  }
  p:last-child { margin-bottom: 0; }
  
  /* Spacing between stacked sections */
  > div + div {
    margin-top: 24px; /* 3 x 8px */
  }
`;

/* Commented out to fix ESLint warning
const ProductFeatureList = styled.ul`
  padding-left: 0;
  margin: 0;
  list-style: none;
  
  li {
    margin-bottom: 8px;
    position: relative;
    padding-left: 16px;
  }
  
  li:before {
    content: '\u2014';
    position: absolute;
    left: 0;
    color: #666;
  }
`;
*/

const SizeChartContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  margin: 0 auto;
  max-width: 600px;
  
  table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 20px;
    border: 2px solid #000;
  }
  
  th, td {
    border: 1px solid #000;
    padding: 12px 16px;
    text-align: center;
    font-size: 1rem;
  }
  
  th {
    background-color: #000;
    color: #fff;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  
  tr:nth-child(even) {
    background-color: #fff;
  }
  
  tr:nth-child(odd) {
    background-color: #fff;
  }
  
  .size-chart-notes {
    margin-top: 20px;
    font-style: italic;
    text-align: center;
    font-size: 0.9rem;
  }
  
  @media (max-width: 768px) {
    th, td {
      padding: 8px;
      font-size: 0.9rem;
    }
  }
  
  @media (max-width: 480px) {
    th, td {
      padding: 6px 4px;
      font-size: 0.8rem;
    }
  }
`;

const ProductDetail = () => {
  const { handle } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('description');
  const [sizeChartData, setSizeChartData] = useState(null);
  const [addedToCart, setAddedToCart] = useState(false);

  // Extract description lines as array for reliable rendering and spacing
  const descriptionItems = React.useMemo(() => {
    if (!product || !product.descriptionHtml) return [];
    try {
      let s = product.descriptionHtml;
      // Mark all potential bullet separators with a sentinel so we can split reliably
      s = s
        .replace(/<li[^>]*>/gi, '|SEP|')
        .replace(/<\/li>/gi, '|SEP|')
        .replace(/<br\s*\/?>/gi, '|SEP|')
        .replace(/<\/p>/gi, '|SEP|')
        .replace(/<span[^>]*>/gi, '|SEP|')
        .replace(/<\/(span|div)>/gi, '|SEP|')
        .replace(/<(p|div)[^>]*>/gi, '')
        .replace(/&(?:mdash|#8212|ndash|#8211|bull|#8226|middot|#183);/gi, '|SEP|')
        .replace(/[\u2014\u2013\u2022\u00B7]/g, '|SEP|')
        .replace(/\u00A0/g, ' ')
        .replace(/\r/g, '');

      // Strip any remaining tags
      s = s.replace(/<[^>]+>/g, '');
      // Collapse duplicate sentinels
      s = s.replace(/\|SEP\|+/g, '|SEP|');

      const items = s
        .split(/\|?SEP\|/)
        .map(t => t
          .replace(/\|?SEP\|?/g, '') // remove any leftover sentinel fragments
          .replace(/^[\u2014\u2013\-•·]\s*/, '') // strip leading dash if present
          .trim()
        )
        .filter(Boolean);
      return items;
    } catch {
      return [];
    }
  }, [product]);

  const fetchProduct = useCallback(async () => {
    const productQuery = `
      query getProductByHandle($handle: String!) {
        product(handle: $handle) {
          id
          title
          handle
          descriptionHtml
          images(first: 10) {
            edges {
              node {
                src
                altText
              }
            }
          }
          options(first: 10) {
            id
            name
            values
          }
          variants(first: 250) {
            edges {
              node {
                id
                title
                availableForSale
                priceV2 {
                  amount
                  currencyCode
                }
                image {
                  src
                  altText
                }
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
          metafield(namespace: "custom", key: "size_chart") {
            value
            type
          }
        }
      }
    `;

    try {
      const { data } = await client.request(productQuery, { variables: { handle } });
      if (data.product) {
        setProduct(data.product);
        
        // Process size chart metafield if it exists
        if (data.product.metafield) {
          console.log('Found metafield:', data.product.metafield);
          const { value, type } = data.product.metafield;
          console.log('Metafield type:', type, 'value:', value);
          
          if (type === 'json') {
            try {
              // Parse the JSON data
              console.log('Attempting to parse JSON:', value);
              const sizeChartJson = JSON.parse(value);
              console.log('Parsed JSON:', sizeChartJson);
              
              // Generate HTML table from JSON
              if (sizeChartJson && sizeChartJson.sizes && Array.isArray(sizeChartJson.sizes)) {
                let tableHtml = '<table><thead><tr>';
                
                // Get all possible columns from the first size object
                const firstSize = sizeChartJson.sizes[0];
                const columns = Object.keys(firstSize);
                
                // Create table headers
                columns.forEach(col => {
                  tableHtml += `<th>${col.charAt(0).toUpperCase() + col.slice(1)}</th>`;
                });
                tableHtml += '</tr></thead><tbody>';
                
                // Add rows for each size
                sizeChartJson.sizes.forEach(size => {
                  tableHtml += '<tr>';
                  columns.forEach(col => {
                    tableHtml += `<td>${size[col] || ''}</td>`;
                  });
                  tableHtml += '</tr>';
                });
                tableHtml += '</tbody></table>';
                
                // Add notes if available
                if (sizeChartJson.notes) {
                  tableHtml += `<p class="size-chart-notes">${sizeChartJson.notes}</p>`;
                }
                
                setSizeChartData(tableHtml);
              }
            } catch (error) {
              console.error('Error parsing size chart JSON:', error);
              setSizeChartData(null);
            }
          } else if (type === 'html') {
            setSizeChartData(value);
          } else if (type === 'file_reference') {
            // For file references, you might need another query to get the file URL
            setSizeChartData(`<img src="${value}" alt="Size Chart" />`);
          }
        }
        
        // Set default options to the first value of each option
        const initialOptions = {};
        data.product.options.forEach(option => {
          if (option.values.length > 0) {
            initialOptions[option.name] = option.values[0];
          }
        });
        setSelectedOptions(initialOptions);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    }
  }, [handle]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  useEffect(() => {
    if (product && product.variants) {
      const variant = product.variants.edges.find(({ node }) => {
        return node.selectedOptions.every(selectedOption => {
          return selectedOptions[selectedOption.name] === selectedOption.value;
        });
      });
      setSelectedVariant(variant ? variant.node : null);
    }
  }, [selectedOptions, product]);

  const handleOptionChange = (optionName, value) => {
    setSelectedOptions(prevOptions => ({ ...prevOptions, [optionName]: value }));
  };
  
  const isOptionValueAvailable = (optionName, optionValue) => {
    if (!product) return false;
    
    // Check if this option value is available in any variant
    return product.variants.edges.some(({ node }) => {
      const matchesCurrentSelections = node.selectedOptions.every(selectedOption => {
        // Skip the current option we're checking
        if (selectedOption.name === optionName) return selectedOption.value === optionValue;
        
        // For other options, check if they match the currently selected options
        const currentlySelected = selectedOptions[selectedOption.name];
        return !currentlySelected || currentlySelected === selectedOption.value;
      });
      
      return matchesCurrentSelections && node.availableForSale;
    });
  };

  const createNewCart = async (lines) => {
    try {
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timed out')), 8000)
      );
      
      const fetchPromise = client.request(
        `mutation cartCreate($lines: [CartLineInput!]) {
          cartCreate(input: {lines: $lines}) {
            cart {
              id
              checkoutUrl
            }
            userErrors {
              field
              message
            }
          }
        }`,
        { variables: { lines } }
      );
      
      const { data, errors } = await Promise.race([fetchPromise, timeoutPromise]);

      if (errors || data.cartCreate.userErrors.length > 0) {
        const errorMsg = errors ? errors[0].message : data.cartCreate.userErrors[0].message;
        throw new Error(errorMsg || 'Failed to create cart');
      }

      localStorage.setItem('cartId', data.cartCreate.cart.id);
      
      // Dispatch a custom event to notify other components that the cart has been updated
      window.dispatchEvent(new CustomEvent('cartUpdated'));
      
      // Show success feedback
      setAddedToCart(true);
      setTimeout(() => setAddedToCart(false), 3000);
    } catch (error) {
      console.error('Error creating cart:', error);
      throw error; // Propagate error to be handled by the caller
    }
  };

  const addToCart = async () => {
    if (!selectedVariant) {
      setError("Please select a variant.");
      return;
    }
    
    setLoading(true);
    setError(null);
    
    const cartId = localStorage.getItem('cartId');
    const lines = [{ merchandiseId: selectedVariant.id, quantity: 1 }];
    
    try {
      if (cartId) {
        // Set up timeout for network issues
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timed out')), 8000)
        );
        
        const fetchPromise = client.request(
          `mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
            cartLinesAdd(cartId: $cartId, lines: $lines) {
              cart {
                id
                checkoutUrl
              }
              userErrors {
                field
                message
              }
            }
          }`,
          { variables: { cartId, lines } }
        );
        
        const { data, errors } = await Promise.race([fetchPromise, timeoutPromise]);

        if (errors || (data.cartLinesAdd && data.cartLinesAdd.userErrors.length > 0)) {
          const errorMsg = errors ? errors[0].message : data.cartLinesAdd.userErrors[0].message;
          throw new Error(errorMsg || 'Failed to add item to cart');
        }

        // Dispatch a custom event to notify other components that the cart has been updated
        window.dispatchEvent(new CustomEvent('cartUpdated'));
        
        // Show success feedback
        setAddedToCart(true);
        setTimeout(() => setAddedToCart(false), 3000);
      } else {
        await createNewCart(lines);
      }
    } catch (error) {
      console.error('Error with cart operation:', error);
      setError(error.message || 'Failed to update cart. Please try again.');
      
      // If cart ID is invalid, create a new one
      if (error.message && (error.message.includes('cart') || error.message.includes('not found'))) {
        localStorage.removeItem('cartId');
        try {
          await createNewCart(lines);
          setError(null); // Clear error if recovery succeeds
        } catch (recoveryError) {
          setError('Could not recover from error. Please refresh the page.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  if (!product) return <div>Loading...</div>;

  // Always prioritize the user's selected image (via thumbnail click) over the variant image
  const activeImageSrc = product.images.edges.length > 0 ? product.images.edges[activeImageIndex]?.node.src : (selectedVariant?.image?.src || '');

  return (
    <PageContainer>

      
      <BreadcrumbNav>
        <a href="/">HOME</a> <span>/</span> <a href="/collections">COLLECTIONS</a> <span>/</span> {product.title}
      </BreadcrumbNav>
      
      <Container>
        <ThumbnailsColumn>
          {product.images.edges.map(({ node }, index) => (
            <ThumbnailImage 
              key={node.src}
              src={node.src}
              active={activeImageIndex === index}
              onClick={() => setActiveImageIndex(index)}
            />
          ))}
        </ThumbnailsColumn>
        
        <ImageSection>
          <ProductImage src={activeImageSrc} alt={product.title} />
        </ImageSection>

        <DetailsSection>
          <Title>{product.title}</Title>
          <Price>${selectedVariant ? parseFloat(selectedVariant.priceV2.amount).toFixed(2) : (product.variants.edges.length > 0 ? parseFloat(product.variants.edges[0].node.priceV2.amount).toFixed(2) : 'N/A')}</Price>
          
          {product.options.map(option => (
            option.name.toLowerCase() === 'size' && (
              <div key={option.id}>
                <SizeLabel>SIZE:</SizeLabel>
                <SizeOptions>
                  {option.values.map(value => {
                    const isAvailable = isOptionValueAvailable(option.name, value);
                    return (
                      <SizeButton
                        key={value}
                        selected={selectedOptions[option.name] === value}
                        disabled={!isAvailable}
                        onClick={() => isAvailable && handleOptionChange(option.name, value)}
                      >
                        {value}
                      </SizeButton>
                    );
                  })}
                </SizeOptions>
              </div>
            )
          ))}
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <AddToCartButton onClick={addToCart} disabled={loading || !selectedVariant}>
            {loading ? 'Adding...' : 'Add to Cart'}
          </AddToCartButton>
          {addedToCart && <SuccessMessage>Added to cart!</SuccessMessage>}
          
          <TabsContainer>
            <TabsHeader>
              <TabsHeaderInner>
                <Tab active={activeTab === 'description'} onClick={() => setActiveTab('description')}>DESCRIPTION</Tab>
                <Tab active={activeTab === 'size-chart'} onClick={() => setActiveTab('size-chart')}>SIZE CHART</Tab>
                <Tab active={activeTab === 'shipping-returns'} onClick={() => setActiveTab('shipping-returns')}>SHIPPING & RETURNS</Tab>
              </TabsHeaderInner>
            </TabsHeader>
            {activeTab === 'description' && (
              <TabContent>
                {descriptionItems.length ? (
                  <ul>
                    {descriptionItems.map((txt, idx) => (
                      <li key={idx}>{txt}</li>
                    ))}
                  </ul>
                ) : (
                  <p>No description available.</p>
                )}
              </TabContent>
            )}
            {activeTab === 'size-chart' && (
              <TabContent className="full-width">
                {sizeChartData ? (
                  <SizeChartContainer dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sizeChartData) }} />
                ) : (
                  <>
                    <div>
                      <h3>FABRIC & FIT</h3>
                      <ul>
                        <li>Material: 100% Cotton</li>
                        <li>Pigment-dyed and garment-washed</li>
                        <li>Slightly oversized and relaxed fit</li>
                      </ul>
                    </div>
                    <div>
                      <h3>CARE INSTRUCTIONS</h3>
                      <ul>
                        <li>Machine wash on gentle</li>
                        <li>Wash with like colors</li>
                        <li>Tumble dry low</li>
                      </ul>
                    </div>
                  </>
                )}
              </TabContent>
            )}
            {activeTab === 'shipping-returns' && (
              <TabContent>
                <div>
                  <h3>SHIPPING</h3>
                  <p>Please allow 3–5 business days for shipment confirmation. Expedited and international shipping are available at checkout for an additional charge.</p>
                </div>
                <div>
                  <h3>RETURNS</h3>
                  <p>Returns are accepted within 14 days of delivery for items in their original condition — unworn, unwashed, and undamaged. Store credit is available under the same conditions, with the window extended to 30 days. All return shipping is paid by the customer.</p>
                </div>
              </TabContent>
            )}
          </TabsContainer>
        </DetailsSection>
      </Container>
    </PageContainer>
  );
};

export default ProductDetail;
